import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PaymentsService } from '../../services/payments.service';

@Component({
	selector: 'app-customer-account-overview-modal',
	templateUrl: './customer-account-overview-modal.component.html',
	styleUrls: ['./customer-account-overview-modal.component.scss'],
})
export class CustomerAccountOverviewModalComponent implements OnInit {
	debtorno = '';
	branchcode = '';
	customerTitle = '';

	loading = true;
	loadError: string | null = null;

	arData: any = null;
	cards: any[] = [];

	constructor(
		public activeModal: NgbActiveModal,
		private paymentsService: PaymentsService,
	) {}

	ngOnInit(): void {
		if (!this.debtorno || !this.branchcode) {
			this.loading = false;
			this.loadError = 'Missing customer or branch.';
			return;
		}
		this.load();
	}

	private load(): void {
		this.loading = true;
		this.loadError = null;

		forkJoin({
			ar: this.paymentsService.getCustomerAccountReceivables(this.debtorno, this.branchcode).pipe(
				catchError(() => of(null)),
			),
			profile: this.paymentsService.loadProfile(this.debtorno, this.branchcode).pipe(
				catchError(() => of({ cards: [] })),
			),
		})
			.pipe(
				finalize(() => {
					this.loading = false;
				}),
			)
			.subscribe({
				next: ({ ar, profile }) => {
					if (!ar) {
						this.loadError = 'Could not load account receivables. Check permissions or try again.';
						return;
					}
					this.arData = ar;
					const profilePayload = profile && typeof profile === 'object' ? (profile as { cards?: unknown[] }) : null;
					this.cards = Array.isArray(profilePayload?.cards) ? (profilePayload.cards as any[]) : [];
				},
				error: () => {
					this.loadError = 'Request failed.';
				},
			});
	}

	get aged(): any {
		return this.arData?.aged || {};
	}

	get termsLabel(): string {
		const t = this.arData?.terms;
		if (!t) {
			return '';
		}
		if (typeof t === 'string') {
			return t;
		}
		return t.terms != null ? String(t.terms) : '';
	}

	get openDebits(): any[] {
		const tx = this.arData?.transactions;
		return Array.isArray(tx) ? tx : [];
	}

	get openCredits(): any[] {
		const p = this.arData?.payments;
		return Array.isArray(p) ? p : [];
	}

	openDebitTotal(): number {
		return this.openDebits.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);
	}

	openCreditTotal(): number {
		return this.openCredits.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);
	}

	salespersonDisplay(row: any): string {
		const name = row?.salespersonname != null ? String(row.salespersonname).trim() : '';
		const code = row?.salesperson != null ? String(row.salesperson).trim() : '';
		if (name && code) {
			return `${name} (${code})`;
		}
		if (name) {
			return name;
		}
		if (code) {
			return code;
		}
		return '—';
	}

	/** Saved card(s) on customer payment profile (from loadProfile). */
	hasProfileCards(): boolean {
		return Array.isArray(this.cards) && this.cards.length > 0;
	}

	/** This open document has a linked gateway / CC transaction record. */
	hasLinkedCardPayment(row: any): boolean {
		if (row?.has_linked_cc_payment === true) {
			return true;
		}
		const cc = row?.cctrans;
		if (!cc || typeof cc !== 'object') {
			return false;
		}
		const id = cc.id;
		if (id !== undefined && id !== null && id !== '') {
			return true;
		}
		const rid = cc.response_id;
		return rid != null && String(rid).trim() !== '';
	}

	cardColumnTooltip(row: any): string {
		const parts: string[] = [];
		parts.push(
			this.hasProfileCards()
				? `Payment profile: ${this.cards.length} saved card(s).`
				: 'No saved cards on payment profile.',
		);
		parts.push(
			this.hasLinkedCardPayment(row)
				? 'This document has a linked card/gateway payment record.'
				: 'No linked card payment on this document.',
		);
		return parts.join(' ');
	}

	displayCardLine(card: any): string {
		const brand = card?.card_type ? String(card.card_type) : 'Card';
		const last = card?.last_four != null && String(card.last_four).trim() !== ''
			? `•••• ${card.last_four}`
			: (card?.display_number != null ? String(card.display_number) : (card?.number != null ? String(card.number) : ''));
		const exp =
			card?.expiry_month && card?.expiry_year
				? ` exp ${String(card.expiry_month).padStart(2, '0')}/${String(card.expiry_year).slice(-2)}`
				: '';
		return `${brand} ${last}${exp}`.trim();
	}

	trackByTrans(_i: number, row: any): string {
		return row?.id != null ? String(row.id) : `${row?.transno}-${row?.trandate}`;
	}
}
